'use client';

import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSession } from 'next-auth/react';

import { useFanfics } from '@/hooks/use-fanfics';
import { getPermalinkListingSlugs } from '@/features/seo-listing/consts';
import type { SeoResolveData } from '@/features/seo-listing/types';
import type { FilterState } from '../store';
import type { ListingRestoreSnapshot } from '../types';
import {
  buildPermalinkAwareListingFilters,
  type FanficDisplay,
  getBrowserPathname,
  mapRawFanficsToDisplay,
  transformFanficData,
} from '../utils';

interface UseListingFanficsLoaderParams {
  isOriginals: boolean;
  filterState: FilterState;
  debouncedSearchQuery: string;
  listingPathEpoch: number;
  isSeoPendingRef: RefObject<boolean>;
  isFirstRenderRef: RefObject<boolean>;
  listingRestoreSnapshot: ListingRestoreSnapshot | null;
  initialFanfics: FanficDisplay[];
  initialTotalPages: number;
  initialTotalItems: number;
  initialSeoPage: SeoResolveData | null;
  initialPage: number;
  listingWorksCount: number | null;
  setListingWorksCount: Dispatch<SetStateAction<number | null>>;
  setSeoPage: Dispatch<SetStateAction<SeoResolveData | null>>;
  setSeoPageTitle: Dispatch<SetStateAction<string | null>>;
}

/**
 * Owns the listing results state (fanfics, pages, loading flags), re-syncs it
 * when SSR props change, and refetches whenever the filter state changes.
 */
export const useListingFanficsLoader = ({
  isOriginals,
  filterState,
  debouncedSearchQuery,
  listingPathEpoch,
  isSeoPendingRef,
  isFirstRenderRef,
  listingRestoreSnapshot,
  initialFanfics,
  initialTotalPages,
  initialTotalItems,
  initialSeoPage,
  initialPage,
  listingWorksCount,
  setListingWorksCount,
  setSeoPage,
  setSeoPageTitle,
}: UseListingFanficsLoaderParams) => {
  // Initialize from initialFanfics when they are provided
  const [fanfics, setFanfics] = useState<FanficDisplay[]>(() =>
    mapRawFanficsToDisplay(initialFanfics),
  );
  const [totalPages, setTotalPages] = useState(initialTotalPages);
  const [hasLoaded, setHasLoaded] = useState(!!initialFanfics?.length);
  const [isRefetching, setIsRefetching] = useState(false);

  // When SSR listing props change (router.push search/sort), replace client
  // grid state during render instead of syncing in an Effect.
  const [ssrListingSnapshot, setSsrListingSnapshot] = useState({
    fanfics: initialFanfics,
    totalPages: initialTotalPages,
    seoPage: initialSeoPage,
    totalItems: initialTotalItems,
  });
  if (
    !listingRestoreSnapshot &&
    (initialFanfics !== ssrListingSnapshot.fanfics ||
      initialTotalPages !== ssrListingSnapshot.totalPages ||
      initialSeoPage !== ssrListingSnapshot.seoPage ||
      initialTotalItems !== ssrListingSnapshot.totalItems)
  ) {
    // Soft Back: URL still has ?page=X from replaceState, but Next remounts with
    // SSR page 1 — do not clobber client state that we restore from URL.
    const urlPage =
      typeof window !== 'undefined'
        ? parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10)
        : initialPage;
    if (urlPage === initialPage) {
      setSsrListingSnapshot({
        fanfics: initialFanfics,
        totalPages: initialTotalPages,
        seoPage: initialSeoPage,
        totalItems: initialTotalItems,
      });
      setFanfics(mapRawFanficsToDisplay(initialFanfics));
      setTotalPages(initialTotalPages);
      setSeoPage(initialSeoPage);
      setSeoPageTitle(initialSeoPage?.title ?? null);
      setListingWorksCount(initialTotalItems ?? null);
      setHasLoaded(true);
      setIsRefetching(false);
    }
  }

  const { data: session } = useSession();

  const { getFanfics, error } = useFanfics({
    accessToken: session?.accessToken,
  });

  const abortControllerRef = useRef<AbortController | null>(null);
  const isLoadingResults = isRefetching || !hasLoaded;

  const loadFanfics = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsRefetching(true);

    try {
      // While SEO redirect is in flight, the address bar still shows the
      // previous permalink. Locking fandomSlug/characterSlug/tagSlug from that
      // stale path would fetch the old entity's works (DEV-488: grid stays one
      // fandom behind). Prefer numeric ids from already-updated filterState
      // until pushState settles; onFiltersRestored often skips a second fetch.
      const listingSlugs = isSeoPendingRef.current
        ? null
        : getPermalinkListingSlugs(getBrowserPathname());
      const result = await getFanfics(
        isOriginals ? 'original' : 'fanfic',
        filterState.currentPage,
        20,
        {
          ...buildPermalinkAwareListingFilters(listingSlugs, {
            selectedFandoms: filterState.selectedFandoms,
            selectedTags: filterState.selectedTags,
            selectedUniverses: filterState.selectedUniverses,
            selectedStatuses: filterState.selectedStatuses,
            selectedRatings: filterState.selectedRatings,
            selectedDirections: filterState.selectedDirections,
            selectedCharacters: filterState.selectedCharacters,
            selectedPairingCharacters: filterState.selectedPairingCharacters,
            search: debouncedSearchQuery,
            sortBy: filterState.sortBy,
          }),
          signal: abortController.signal,
        },
      );

      if (!abortController.signal.aborted && abortControllerRef.current === abortController) {
        // Transform data (memoization via a stable function reference)
        const transformedFanfics = result.fanfics.map(transformFanficData);
        setFanfics(transformedFanfics);
        setTotalPages(result.totalPages);
        setListingWorksCount(result.totalItems ?? result.totalDocs ?? listingWorksCount);
        setHasLoaded(true);
        setIsRefetching(false);
      }
    } catch (err) {
      if (abortControllerRef.current === abortController && !abortController.signal.aborted) {
        console.error('Failed to load fanfics:', err);
        setIsRefetching(false);
      }
    }
  };

  // Load fanfics when parameters change
  useEffect(() => {
    // Skip the first load if SSR already served the same page as in the URL.
    // Soft Back: URL ?page=X with SSR page 1 — do not skip, otherwise page 1 stays.
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      const urlPage = parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10);
      const isSsrAlignedWithUrl =
        urlPage === initialPage && filterState.currentPage === initialPage;
      if (initialFanfics?.length && isSsrAlignedWithUrl) {
        return;
      }
    }

    // After the first render always fetch when parameters change
    loadFanfics();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterState.activeTab,
    filterState.currentPage,
    filterState.sortBy,
    debouncedSearchQuery,
    filterState.selectedFandoms,
    filterState.selectedTags,
    filterState.selectedUniverses,
    filterState.selectedStatuses,
    filterState.selectedRatings,
    filterState.selectedDirections,
    filterState.selectedCharacters,
    filterState.selectedPairingCharacters,
    listingPathEpoch,
  ]);

  return {
    fanfics,
    totalPages,
    hasLoaded,
    isRefetching,
    isLoadingResults,
    error,
    setHasLoaded,
    setIsRefetching,
  };
};
